import { ICellRendererParams } from "@ag-grid-community/core";
import {
  Box,
  Flex,
  Link,
  Tooltip,
  useDisclosure,
  useMergeRefs,
} from "@chakra-ui/react";
import {
  CapitalCallFeeModal,
  CapitalCallFeeModalCommonProps,
} from "features/capital-call/CapitalCallFeeModal";
import { useAvailableFeeChargeEventsForCallRetrieve } from "gen/endpoints/fee-charge-event-select/fee-charge-event-select";
import { useFeeListSuspense } from "gen/endpoints/fees/fees";
import { useInvestmentListSuspense } from "gen/endpoints/investment/investment";
import { useTransactionCodeConfigListSuspense } from "gen/endpoints/transactions/transactions";
import { CallOrDistributionEnum } from "gen/model/callOrDistributionEnum";
import { FeeChargeEventCallDetails } from "gen/model/feeChargeEventCallDetails";
import { FundFamilyUseAllocationTableColumnDataCapitalCallUseOfFunds } from "gen/model/fundFamilyUseAllocationTableColumnDataCapitalCallUseOfFunds";
import { FundFamilyUseAllocationTableRow } from "gen/model/fundFamilyUseAllocationTableRow";
import { TransactionCodeConfigListClassification } from "gen/model/transactionCodeConfigListClassification";
import React, { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  FundFamilyUseAllocationTableAmountInfoCellRendererProps,
  FundFamilyUseAllocationTableContext,
} from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableProps";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { SimpleInputErrorElement } from "shared/components/input/select/SimpleInputErrorElement";
import { SimpleSelect } from "shared/components/input/select/SimpleSelect";
import { SimpleTextInput } from "shared/components/input/text/SimpleTextInput";
import { getMaybernTableCellError } from "shared/components/table/maybern/MaybernTableErrors";
import { MaybernTableFocusCellRendererWrapper } from "shared/components/table/maybern/MaybernTableFocusCellRendererWrapper";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import {
  formatMoneyWithoutCurrencySymbol,
  sumMoneyStrings,
} from "shared/utils/MoneyUtils";
import invariant from "shared/utils/TinyInvariant";

export const CapitalCallFeeSelectCellRenderer: React.FC<
  ICellRendererParams<
    FundFamilyUseAllocationTableRow,
    FundFamilyUseAllocationTableColumnDataCapitalCallUseOfFunds
  >
> = (params) => {
  const { t } = useTranslation(["capitalCall"]);

  const { tableDef }: FundFamilyUseAllocationTableContext = params.context;
  const fundFamilyId = tableDef.fund_family.fund_family_id;

  const { data } = useFeeListSuspense(fundFamilyId);

  const ref = useRef<never>(null);

  return (
    <MaybernTableFocusCellRendererWrapper params={params} inputRef={ref}>
      <SimpleSelect
        ref={ref}
        title={t("capitalCall:linkToFee")}
        value={params.value?.fee_id ?? undefined}
        onChangeValue={(value) => {
          params.setValue!({ fee_id: value.toString() });
        }}
        variant="simple-table"
        isDisabled={params.node.data?.is_read_only || false}
        error={getMaybernTableCellError(params)}
      >
        {data.map(({ id, name }) => (
          <SelectOption value={id} key={id}>
            {name}
          </SelectOption>
        ))}
      </SimpleSelect>
    </MaybernTableFocusCellRendererWrapper>
  );
};

export const CapitalCallTransactionSelectCellRenderer: React.FC<
  ICellRendererParams<
    FundFamilyUseAllocationTableRow,
    FundFamilyUseAllocationTableColumnDataCapitalCallUseOfFunds
  >
> = (params) => {
  const { t } = useTranslation(["capitalCall"]);

  const { tableDef }: FundFamilyUseAllocationTableContext = params.context;
  const fundFamilyId = tableDef.fund_family.fund_family_id;

  const { data } = useTransactionCodeConfigListSuspense(fundFamilyId, {
    call_or_distribution: CallOrDistributionEnum.call,
    classification: params.data?.category_id
      ? categoryIdToClassification[params.data.category_id]
      : undefined,
  });

  const ref = useRef<never>(null);

  return (
    <MaybernTableFocusCellRendererWrapper params={params} inputRef={ref}>
      <SimpleSelect
        ref={ref}
        title={t("capitalCall:selectTransactionCode")}
        value={params.value?.transaction_code_id ?? undefined}
        onChangeValue={(value) => {
          params.setValue!({
            /* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */
            transaction_code_id: value?.toString() ?? undefined,
          });
        }}
        variant="simple-table"
        isDisabled={params.node.data?.is_read_only || false}
        error={getMaybernTableCellError(params)}
      >
        {data.map(({ transaction_code_id, rich_name }) => (
          <SelectOption value={transaction_code_id} key={transaction_code_id}>
            {rich_name}
          </SelectOption>
        ))}
      </SimpleSelect>
    </MaybernTableFocusCellRendererWrapper>
  );
};

export const CapitalCallNotesCellRenderer: React.FC<ICellRendererParams> = (
  params,
) => {
  const ref = useRef<never>(null);
  const { t } = useTranslation(["capitalCall"]);

  return (
    <MaybernTableFocusCellRendererWrapper params={params} inputRef={ref}>
      <SimpleTextInput
        ref={ref}
        name={t("capitalCall:enterNotesOptional")}
        title={t("capitalCall:enterNotesOptional")}
        placeholder={t("capitalCall:enterNotesOptional")}
        value={params.value}
        onChangeValue={params.setValue}
        variant="simple-table"
      />
    </MaybernTableFocusCellRendererWrapper>
  );
};

export const CapitalCallInvestmentSelectCellRenderer: React.FC<
  ICellRendererParams<FundFamilyUseAllocationTableRow>
> = (params) => {
  const { t } = useTranslation(["capitalCall"]);

  const { tableDef }: FundFamilyUseAllocationTableContext = params.context;
  const fundFamilyId = tableDef.fund_family.fund_family_id;

  const { data } = useInvestmentListSuspense(fundFamilyId, {
    is_allocable: true,
  });

  const ref = useRef<never>(null);

  return (
    <MaybernTableFocusCellRendererWrapper params={params} inputRef={ref}>
      <SimpleSelect
        ref={ref}
        title={t("capitalCall:selectInvestmentOptional")}
        value={params.value}
        onChangeValue={params.setValue}
        variant="simple-table"
        isDisabled={params.node.data?.is_read_only || false}
      >
        {data.map(({ id, legal_name }) => (
          <SelectOption value={id!} key={id}>
            {legal_name}
          </SelectOption>
        ))}
      </SimpleSelect>
    </MaybernTableFocusCellRendererWrapper>
  );
};

export const CapitalCallManagementFeeAmountCellRenderer = React.forwardRef<
  HTMLDivElement,
  FundFamilyUseAllocationTableAmountInfoCellRendererProps
>(({ fundFamilyOrFundDef, amountTypeDef, ...params }, ref) => {
  invariant("fund_id" in fundFamilyOrFundDef);
  const { t } = useTranslation(["capitalCall"]);
  const { fundFamilyId, fundFamilyEventId } = useRequiredStringParams();

  const localRef = useRef<HTMLDivElement>(null);
  const mergedRef = useMergeRefs(ref, localRef);

  const columnData = params.node.data?.column_data;
  invariant(columnData?.type === "capital_call");

  const feeId = columnData.use_of_funds.fee_id;
  const fundId = fundFamilyOrFundDef.fund_id;
  const amountMeta = params.node.data?.funds[fundId]?.[amountTypeDef.id]
    ?.meta ?? {
    type: "management_fee",
  };
  invariant(amountMeta.type === "management_fee");

  const { data: availableEventData } =
    useAvailableFeeChargeEventsForCallRetrieve(
      fundFamilyId,
      {
        fee_id: feeId!,
        fund_id: fundId,
      },
      { query: { enabled: !!feeId } },
    );

  const { isOpen, onOpen, onClose } = useDisclosure();

  const hasOutstandingEvents =
    availableEventData && availableEventData.funds[fundId].length > 0;

  const error = getMaybernTableCellError(params);

  const isDisabled =
    !columnData.use_of_funds.fee_id || params.data?.is_auto_allocate;

  const feeButton = (
    <Flex
      w="100%"
      ref={mergedRef}
      justify="flex-end"
      cursor={isDisabled ? "not-allowed" : undefined}
    >
      <Link
        aria-disabled={isDisabled || !hasOutstandingEvents}
        onClick={onOpen}
      >
        {amountMeta.total_fees_to_call
          ? formatMoneyWithoutCurrencySymbol(amountMeta.total_fees_to_call)
          : t("capitalCall:selectFees")}
      </Link>
      {error && <CapitalCallManagementFeeAmountCellError error={error} />}
      {isOpen && feeId && availableEventData && (
        <CapitalCallFeeModal
          onCloseComplete={onClose}
          fundId={fundId}
          availableEvents={availableEventData}
          fundFamilyId={fundFamilyId}
          fundFamilyEventId={fundFamilyEventId}
          feeChargeEventIds={Object.keys(
            amountMeta.fee_charge_event_id_to_call_details || {},
          ).map(([feeChargeEventId]) => feeChargeEventId)}
          onFeeChargeEventSelect={(feeChargeEventIds, outstandingAmounts) => {
            params.setValue!({
              amount: outstandingAmounts[fundId] ?? "",
              meta: {
                type: "management_fee",
                fee_charge_event_id_to_call_details:
                  feeChargeEventIds?.reduce(
                    (accumulator, feeChargeEventId) => {
                      accumulator[feeChargeEventId] = {
                        call_for_management_fee_interest: false,
                        call_for_fees: true,
                      };
                      return accumulator;
                    },
                    {} as Record<string, FeeChargeEventCallDetails>,
                  ) || ({} as Record<string, FeeChargeEventCallDetails>),
                total_fees_to_call: outstandingAmounts[fundId] ?? "",
              },
            });
          }}
        />
      )}
    </Flex>
  );

  return !hasOutstandingEvents && !isDisabled ? (
    <Tooltip
      label={t("capitalCall:noCurrentOutstandingFees")}
      hasArrow={true}
      openDelay={0}
      closeDelay={0}
    >
      {feeButton}
    </Tooltip>
  ) : (
    feeButton
  );
});

CapitalCallManagementFeeAmountCellRenderer.displayName =
  "CapitalCallManagementFeeAmountCellRenderer";

export const CapitalCallManagementFeeFundFamilyAmountCellRenderer =
  React.forwardRef<
    HTMLDivElement,
    FundFamilyUseAllocationTableAmountInfoCellRendererProps
  >(({ fundFamilyOrFundDef, amountTypeDef, ...params }, ref) => {
    invariant("fund_family_id" in fundFamilyOrFundDef);

    const { fundFamilyId, fundFamilyEventId } = useRequiredStringParams();
    const { t } = useTranslation(["capitalCall"]);

    const localRef = useRef<HTMLDivElement>(null);
    const mergedRef = useMergeRefs(ref, localRef);

    const columnData = params.node.data?.column_data;
    invariant(columnData?.type === "capital_call");

    const feeId = columnData.use_of_funds.fee_id;
    const amountMeta = params.node.data?.fund_family[amountTypeDef.id]
      ?.meta ?? {
      type: "management_fee",
    };
    invariant(amountMeta.type === "management_fee");

    const { data: availableEventData } =
      useAvailableFeeChargeEventsForCallRetrieve(
        fundFamilyId,
        {
          fee_id: feeId!,
        },
        { query: { enabled: !!feeId } },
      );

    const { isOpen, onOpen, onClose } = useDisclosure();

    const hasOutstandingEvents =
      availableEventData &&
      Object.values(availableEventData.funds).some(
        (events) => events.length > 0,
      );

    const error = getMaybernTableCellError(params);

    const onFeeChargeEventSelect = useCallback<
      CapitalCallFeeModalCommonProps["onFeeChargeEventSelect"]
    >(
      (feeChargeEventIds, outstandingAmounts) => {
        // update fund rows
        const fund_family_amount = sumMoneyStrings(
          Object.values(outstandingAmounts).flat(1),
        );
        (params.context as FundFamilyUseAllocationTableContext).onRowChange({
          row: {
            ...params.node.data!,
            fund_family: {
              ...params.node.data?.fund_family,
              [amountTypeDef.id]: {
                amount: fund_family_amount,
                meta: {
                  type: "management_fee",
                  fee_charge_event_id_to_call_details:
                    feeChargeEventIds?.reduce(
                      (accumulator, feeChargeEventId) => {
                        accumulator[feeChargeEventId] = {
                          call_for_management_fee_interest: false,
                          call_for_fees: true,
                        };
                        return accumulator;
                      },
                      {} as Record<string, FeeChargeEventCallDetails>,
                    ) || ({} as Record<string, FeeChargeEventCallDetails>),
                  total_fees_to_call: fund_family_amount,
                },
              },
            },
            funds: Object.entries(params.node.data?.funds ?? []).reduce(
              (fundsObject, [fundId, fundAmount]) => ({
                ...fundsObject,
                [fundId]: {
                  called: {
                    ...fundAmount.called,
                    amount: outstandingAmounts[fundId] ?? "",
                    meta: {
                      ...fundAmount.called.meta,
                      total_fees_to_call: outstandingAmounts[fundId] ?? "",
                      fee_charge_event_id_to_call_details:
                        feeChargeEventIds?.reduce(
                          (accumulator, feeChargeEventId) => {
                            accumulator[feeChargeEventId] = {
                              call_for_management_fee_interest: false,
                              call_for_fees: true,
                            };
                            return accumulator;
                          },
                          {} as Record<string, FeeChargeEventCallDetails>,
                        ) || ({} as Record<string, FeeChargeEventCallDetails>),
                    },
                  },
                },
              }),
              {},
            ),
          },
        });
      },
      [amountTypeDef.id, params.context, params.node.data],
    );

    const isDisabled =
      !columnData.use_of_funds.fee_id || !params.data?.is_auto_allocate;

    const feeButton = (
      <Flex
        ref={mergedRef}
        w="100%"
        justify="flex-end"
        cursor={isDisabled ? "not-allowed" : undefined}
      >
        <Link
          aria-disabled={isDisabled || !hasOutstandingEvents}
          onClick={onOpen}
        >
          {amountMeta.total_fees_to_call
            ? formatMoneyWithoutCurrencySymbol(amountMeta.total_fees_to_call)
            : t("capitalCall:selectFees")}
        </Link>
        {error && <CapitalCallManagementFeeAmountCellError error={error} />}
        {isOpen && feeId && availableEventData && (
          <CapitalCallFeeModal
            onCloseComplete={onClose}
            fundFamilyId={fundFamilyId}
            fundFamilyEventId={fundFamilyEventId}
            availableEvents={availableEventData}
            feeChargeEventIds={Object.keys(
              amountMeta.fee_charge_event_id_to_call_details || {},
            ).map(([feeChargeEventId]) => feeChargeEventId)}
            onFeeChargeEventSelect={onFeeChargeEventSelect}
          />
        )}
      </Flex>
    );
    return !hasOutstandingEvents && !isDisabled ? (
      <Tooltip
        label={t("capitalCall:noCurrentOutstandingFees")}
        hasArrow={true}
        openDelay={0}
        closeDelay={0}
      >
        {feeButton}
      </Tooltip>
    ) : (
      feeButton
    );
  });

CapitalCallManagementFeeFundFamilyAmountCellRenderer.displayName =
  "CapitalCallManagementFeeFundFamilyAmountCellRenderer";

export const categoryIdToClassification: Record<
  string,
  TransactionCodeConfigListClassification
> = {
  investment: TransactionCodeConfigListClassification.investment,
  expenses: TransactionCodeConfigListClassification.expenses,
  taxes: TransactionCodeConfigListClassification.taxes,
  capital_reserves: TransactionCodeConfigListClassification.working_capital,
  bridge_financing: TransactionCodeConfigListClassification.bridge_financing,
};

const CapitalCallManagementFeeAmountCellError: React.FC<{
  error: string;
}> = ({ error }) => (
  <Box
    w="24px"
    h="24px"
    ml="8px"
    mr="-15px"
    sx={{ svg: { w: "16px", h: "16px" } }}
  >
    <SimpleInputErrorElement error={error} />
  </Box>
);
